/**
 * Platform observability: the backend contract, plus the helpers the Platform page
 * uses to read it. Nothing here talks to AWS.
 *
 * Served by backend/api/routes/observability.py; services/observability/views.py
 * builds each response and is the reference for its fields. All endpoints: GET,
 * admins only (analysts are refused), read-only, JSON. The backend caches each
 * response for 30-60 s, because each one fans out to CloudWatch, ECS, EKS, RDS and
 * ELB APIs that are rate-limited and billed per request.
 *
 * Every response carries `warnings`: the sources it could not read (a missing
 * permission, a throttled API, a sampler that has not written yet). What could be
 * read is returned as usual and what could not is left empty, so the page says what
 * is missing instead of drawing it as zero.
 *
 *   GET /api/observability/overview?range=R
 *   GET /api/observability/services/<service-id>/metrics?range=R
 *   GET /api/observability/kubernetes?range=R
 *   GET /api/observability/database?range=R
 *   GET /api/observability/edge?range=R
 *   GET /api/observability/workspaces?fs_id=F&limit=N   (each user's measured workspace size)
 *   GET /api/observability/workspaces/over-cap?fs_id=F&offset=O&limit=N   (users over their cap)
 *   GET /api/observability/jobs
 *   GET /api/observability/jobs/<job-key>/runs?limit=N&before=ID
 *   GET /api/observability/deployments
 *   GET /api/observability/alarms
 *
 * R is one of RANGES[].value. Time is epoch milliseconds, UTC, everywhere.
 */

export const OBSERVABILITY_BASE = '/api/observability';

export const ENDPOINTS = {
  overview: `${OBSERVABILITY_BASE}/overview`,
  serviceMetrics: (id) => `${OBSERVABILITY_BASE}/services/${encodeURIComponent(id)}/metrics`,
  kubernetes: `${OBSERVABILITY_BASE}/kubernetes`,
  database: `${OBSERVABILITY_BASE}/database`,
  edge: `${OBSERVABILITY_BASE}/edge`,
  workspaces: `${OBSERVABILITY_BASE}/workspaces`,
  workspacesOverCap: `${OBSERVABILITY_BASE}/workspaces/over-cap`,
  jobs: `${OBSERVABILITY_BASE}/jobs`,
  jobRuns: (key) => `${OBSERVABILITY_BASE}/jobs/${encodeURIComponent(key)}/runs`,
  deployments: `${OBSERVABILITY_BASE}/deployments`,
  alarms: `${OBSERVABILITY_BASE}/alarms`,
};

// `step` is the bucket the backend requests from GetMetricData (Period). 60 s is the
// finest resolution standard ECS / RDS metrics have; 7 days at 1 h keeps a series at
// 168 points, which the charts draw comfortably. Must match RANGES in
// backend/services/observability/core.py. `refresh` is how often the page polls: every
// minute, since the tiles above the charts are "now" figures, except at 7 days, where a
// minute moves nothing but the last hourly bucket.
export const RANGES = [
  { value: '1h', label: '1h', seconds: 3600, step: 60, refresh: 60 },
  { value: '6h', label: '6h', seconds: 6 * 3600, step: 300, refresh: 60 },
  { value: '24h', label: '24h', seconds: 24 * 3600, step: 300, refresh: 60 },
  { value: '7d', label: '7d', seconds: 7 * 24 * 3600, step: 3600, refresh: 300 },
];

export const rangeFor = (value) => RANGES.find(r => r.value === value) || RANGES[2];

/**
 * @typedef {'proxy'|'bridge'|'core'} ServiceKind
 * @typedef {'ok'|'warn'|'critical'|'idle'} Health
 *
 * @typedef {Object} ServiceSummary          one ECS service (overview.services[])
 * @property {string} id                     short id, e.g. 'proxy-llm' (no env prefix, no fg- infix)
 * @property {string} ecs_service            the real ECS name, e.g. 'nxt-openpaw-d-fg-proxy-llm'
 * @property {ServiceKind} kind
 * @property {'fargate'|'ec2'} runtime
 * @property {string} role                   one line: what it does
 * @property {number} desired                ECS desiredCount
 * @property {number} running                ECS runningCount
 * @property {number} pending                ECS pendingCount
 * @property {number} cpu_units              task-level CPU reservation (1024 = 1 vCPU)
 * @property {number} mem_mib                task-level memory reservation
 * @property {number|null} cpu_pct           service average over the last 5 min, % of reservation
 * @property {number|null} mem_pct           same, memory
 * @property {number[]} spark                ~24 CPU % samples across the range, for the tile sparkline
 * @property {{tag: string, stale: boolean}} image   stale = a running task's digest != :latest's digest
 * @property {{state: 'steady'|'rolling'|'failed', since: number}} deployment   from the PRIMARY deployment
 * @property {boolean} [routed]              false for a service that runs but receives no traffic
 * @property {string} [note]                 operator-facing caveat, shown on the tile
 * @property {number|null} requests_per_min  null for a service with no countable requests
 *           (No per-copy list here: numbering copies needs the task history, which only
 *           the drill-down endpoint should have to read.)
 *
 * @typedef {Object} MetricBlock             one metric for one service, every copy aligned
 * @property {string} metric                 'cpu' | 'memory' | 'net_rx' | 'net_tx' | 'requests' | 'errors_5xx'
 * @property {'percent'|'bytes_per_s'|'per_min'|'count'} unit
 * @property {'avg'|'sum'} aggregate         how the `service` series combines tasks
 * @property {number[]} timestamps           bucket starts, shared by every series below
 * @property {Object<string, Array<number|null>>} series
 *           `service` = the service-level metric (every task that ran in the window,
 *           including ones replaced by a deploy), plus one key per copy in `replicas`
 *           ('copy-1', 'copy-2', ...). A copy's value in a bucket is its holder's
 *           (holderAt below); it is null in every bucket where no task held the copy,
 *           which is what draws the gap while a copy was offline.
 *
 * @typedef {Object} Replica                 one numbered COPY of the service (see "Copies" below)
 * @property {string} id                     'copy-<index>': the series key, the same in every range
 * @property {number} index                  the copy number N, 1-based
 * @property {'RUNNING'|'PENDING'|'STOPPING'|'STOPPED'} status   of the task holding it now;
 *                                           STOPPED = no task holds it now (shown as offline)
 * @property {string|null} task_id           the task holding it now, null when STOPPED
 * @property {string} zone                   of the current task, or of the last one
 * @property {number} started_at             start of the current task, or of the last one
 * @property {number|null} stopped_at        when the last task stopped, null unless STOPPED
 * @property {ReplicaTask[]} tasks           every task that held the copy inside the window, oldest first
 *
 * @typedef {Object} ReplicaTask
 * @property {string} id                     ECS task id (32 hex)
 * @property {number} started_at             DescribeTasks `startedAt` while the task runs; for a
 *                                           stopped one, its first datapoint (clamped to the history)
 * @property {number|null} stopped_at        null while running; else its last datapoint or `stoppedAt`
 * @property {string} zone
 *
 * @typedef {Object} ServiceMetrics          GET /services/<id>/metrics
 * @property {string} service
 * @property {string} range
 * @property {number} step_s
 * @property {Replica[]} replicas            every copy that held a task at any point in the window
 * @property {Object<string, MetricBlock>} metrics   cpu and memory always; net_rx and
 *           net_tx when Container Insights has task records; requests and errors_5xx
 *           once the service's tasks write the per-minute telemetry line
 * @property {string[]} warnings
 *
 * Kubernetes, database and edge follow one pattern: `current` for the tiles, plus
 * `timestamps` and `series` (one array per key, aligned, null where there is no data)
 * for the charts.
 *
 * @typedef {Object} Overview                GET /overview
 * @property {number} generated_at
 * @property {{name: string, region: string, cluster: string, eks_cluster: string}} env
 * @property {ServiceSummary[]} services
 * @property {Object} kubernetes             Kubernetes.current
 * @property {Object} database               Database.current, plus min_acu, max_acu and writer_class
 * @property {{open: number, items: Alarm[]}} alarms
 *
 * Kubernetes (GET /kubernetes), from the control-plane (now) and node_metrics (history,
 * sampled every ~2 min, so the step is at least 120 s):
 *   current  version, nodes_total, nodes_ready, agents_running, agents_pending,
 *            agents_hibernated, balloons, karpenter_cpu_limit, karpenter_cpu_used,
 *            cpu_requested_pct, mem_requested_pct
 *   pools[]  name, manager, purpose, instance_types[], capacity_types[], nodes, pods,
 *            agents, balloons, min, max, cpu_limit
 *   nodes[]  name, pool, instance_type, zone, capacity_type, age_s, pods, agents,
 *            balloons, system_pods, cpu_req_pct, mem_req_pct, status
 *            `pods` is every pod on the node: agents + balloons + system_pods (the
 *            per-node daemons and the cluster's own services). capacity_type and
 *            capacity_types come from the node labels / NodePool requirements /
 *            node group, so a spot or capacity-block pool shows as such.
 *   series   nodes_agents, nodes_gvisor, agents_running, agents_pending,
 *            cpu_requested_pct, mem_requested_pct
 *
 * Database (GET /database): aurora {cluster, engine, serverless, writer_class, min_acu,
 *   max_acu, instances[{id, role, zone, instance_class, serverless}]}; min_acu and max_acu
 *   are null and the acu_* series absent when no instance runs Serverless v2;
 *   proxy {name, auth} or null when the environment runs no RDS Proxy; series and
 *   current: acu_writer, acu_reader, connections, cpu_writer, cpu_reader,
 *   commit_latency_ms, replica_lag_ms, proxy_client_connections, proxy_db_connections,
 *   db_size_bytes; current also max_connections. The reader series are present only
 *   when the cluster has a reader, the proxy series only when there is a proxy.
 *
 * Edge (GET /edge): alb {name, target_groups[{name, service, path, healthy, unhealthy}]},
 *   series requests (per minute), p95_ms, http_4xx, http_5xx (per bucket, the target's
 *   and the load balancer's own); current adds healthy_targets and unhealthy_targets;
 *   efs[] {id, name, shard, users, size_bytes, throughput_bps, percent_io_limit}, the
 *   last two over the last five minutes.
 *
 * Deployments (GET /deployments), all from ECS/ECR in this account, so every env reads
 *   the same: images[] {service, container, repository, running_tag, running_digest,
 *   latest_digest, pushed_at, status: current | stale | idle}; history[] {id, service,
 *   status: successful | in_progress | stopping | stopped | rolling_back | rolled_back |
 *   failed, reason, created_at, started_at, finished_at, rollback, task_definition,
 *   images[{container, repository, tag, digest, expired}]}, newest first, up to 10 per
 *   service (ECS keeps 90 days); events[] {at, service, message}.
 *
 * Jobs (GET /jobs), built by backend/services/observability/jobs.py: jobs[] {key, label,
 *   group, runs_in, description, schedule {kind: eventbridge | loop | retention | cronjob,
 *   expression?, rule?, state?, every_s, source: EventBridge | CronJob | Data Governance |
 *   observed | default}, status: ok | running | failing | interrupted | overdue | skipped |
 *   never | disabled | unrecorded, last_run JobRun|null, last_ok_at, stats_24h {runs, errors,
 *   skipped}|null, history: job_runs | kubernetes | null}. A loop's routine ticks share a
 *   row (JobRun.runs counts them, its summary adds their figures up).
 *   GET /jobs/<key>/runs?limit&before -> {job, runs: JobRun[], source, next_before, keeps?}.
 *   JobRun {id, status, trigger: schedule | manual, started_at, finished_at, duration_ms
 *   (the slowest tick, for a shared row), runs, runner, summary, error}.
 *
 * @typedef {Object} Alarm                   GET /alarms -> {alarms: Alarm[]}
 * @property {string} name
 * @property {'OK'|'ALARM'|'INSUFFICIENT_DATA'} state
 * @property {number} since                  last state change
 * @property {string} namespace
 * @property {string} metric
 * @property {string} threshold              in words, e.g. "above 80 for 2 of 3 periods of 1 min"
 * @property {string} [reason]               only when not OK
 * @property {string|null} service           the service id the alarm is about, when there is one
 */

// ── Health ──────────────────────────────────────────────────────────────────
// Derived client-side from the summary rather than sent by the backend, so every
// view judges a service by the same rules and the rules live in one place.
export const THRESHOLDS = { cpuWarn: 75, cpuCrit: 90, memWarn: 80, memCrit: 90 };

export function serviceHealth(s) {
  return healthWithReasons(s).health;
}

export function healthWithReasons(s) {
  if (!s) return { health: 'idle', reasons: [] };
  if (!s.desired) return { health: 'idle', reasons: ['scaled to zero'] };
  const reasons = [];
  let health = 'ok';
  const bump = (h, why) => {
    reasons.push(why);
    if (h === 'critical' || (h === 'warn' && health === 'ok')) health = h;
  };
  if (s.running < s.desired) bump('critical', `${s.running} of ${s.desired} replicas running`);
  if (s.deployment?.state === 'failed') bump('critical', 'last deployment failed');
  if (s.cpu_pct >= THRESHOLDS.cpuCrit) bump('critical', `CPU at ${Math.round(s.cpu_pct)}%`);
  else if (s.cpu_pct >= THRESHOLDS.cpuWarn) bump('warn', `CPU at ${Math.round(s.cpu_pct)}%`);
  if (s.mem_pct >= THRESHOLDS.memCrit) bump('critical', `memory at ${Math.round(s.mem_pct)}%`);
  else if (s.mem_pct >= THRESHOLDS.memWarn) bump('warn', `memory at ${Math.round(s.mem_pct)}%`);
  if (s.image?.stale) bump('warn', 'running an image older than :latest');
  if (s.deployment?.state === 'rolling') bump('warn', 'deployment in progress');
  return { health, reasons };
}

export const HEALTH_LABEL = { ok: 'Healthy', warn: 'Degraded', critical: 'Critical', idle: 'Idle' };
export const HEALTH_TONE = { ok: 'ok', warn: 'warn', critical: 'danger', idle: 'neutral' };

export const KIND_LABEL = { proxy: 'Proxy', bridge: 'Bridge', core: 'Core' };
export const KIND_GROUP_LABEL = { proxy: 'Proxies', bridge: 'Bridges', core: 'Core' };

// ── Copies ──────────────────────────────────────────────────────────────────
// A service's tasks come and go: a deploy replaces all of them, autoscaling adds
// and removes some, a crashed task gets a replacement. The charts do not draw one
// line per task, because then "copy 3 came back after the scale-in" would look like
// a brand-new replica. They draw one line per COPY: a numbered slot that a sequence
// of tasks holds over time. A copy's line has a gap exactly where no task held it.
//
// The backend owns the numbering and sends the result (ServiceMetrics.replicas).
// The three functions below are the rule, and backend/services/observability/copies.py
// is their line-for-line port: change the two together. The frontend itself only
// calls holderAt, to label a gap "offline" or "no data".

/**
 * A task that stops at most this long after another one started hands its copy over.
 * 30 minutes because that is what DEV's rolling deploys measured: most retire the old
 * task within 10 minutes of the new one starting, and the slowest seen took 17.
 */
export const HANDOVER_MS = 30 * 60 * 1000;
/**
 * A stop time read back from Container Insights is its last one-minute record, so a
 * successor can appear to start just after its predecessor stopped when the two in
 * fact overlapped. A stop this close before the start still counts as a handover.
 */
export const HANDOVER_GRACE_MS = 2 * 60 * 1000;

const endOf = (task) => (task.stopped_at == null ? Infinity : task.stopped_at);
const lastTask = (copy) => copy.tasks[copy.tasks.length - 1];

/**
 * Number a service's tasks into copies. Tasks are taken in order of start (task id
 * breaks ties) and each one:
 *   1. takes over the copy of a task that is still running when it starts (or stopped
 *      no more than HANDOVER_GRACE_MS before) and stops within HANDOVER_MS of it (the
 *      soonest to stop, if several): a rolling deploy, or a replacement started before
 *      the old task drained. No gap;
 *   2. otherwise takes the lowest-numbered copy that is free when it starts, so a
 *      copy that went offline gets its number back at the next scale-out;
 *   3. otherwise opens a new copy.
 * Run it over the whole task history, never over one chart window, or the same task
 * could be copy 2 at 1h and copy 3 at 24h. Rule 1 needs the predecessor's stop, so
 * while a deploy is still rolling the new task shows as an extra copy, and folds
 * into its predecessor's copy on the first refresh after that task stops.
 *
 * @param {Array<{id: string, started_at: number, stopped_at: (number|null)}>} tasks
 * @returns {Array<{index: number, tasks: Object[]}>} by copy number, tasks oldest first
 */
export function assignCopies(tasks) {
  const order = [...tasks].sort((a, b) => (a.started_at - b.started_at)
    || String(a.id).localeCompare(String(b.id)));
  const copies = [];
  order.forEach((task) => {
    const start = task.started_at;
    let pick = -1;
    copies.forEach((c, i) => {
      const end = endOf(lastTask(c));
      if (end > start - HANDOVER_GRACE_MS && end - start <= HANDOVER_MS
          && (pick < 0 || end < endOf(lastTask(copies[pick])))) pick = i;
    });
    if (pick < 0) pick = copies.findIndex(c => endOf(lastTask(c)) <= start);
    if (pick < 0) {
      copies.push({ index: copies.length + 1, tasks: [] });
      pick = copies.length - 1;
    }
    copies[pick].tasks.push(task);
  });
  return copies;
}

/**
 * The Replica[] for one chart window [from, to): every copy that held a task at any
 * point in it, carrying only the tasks that overlap the window. The window is
 * expected to end at "now", so a task with no stop is the copy's current holder.
 */
export function replicasInWindow(copies, from, to) {
  return copies.map((c) => {
    const tasks = c.tasks.filter(t => t.started_at < to && endOf(t) > from);
    if (!tasks.length) return null;
    const last = tasks[tasks.length - 1];
    const live = last.stopped_at == null;
    return {
      id: `copy-${c.index}`,
      index: c.index,
      status: live ? (last.status || 'RUNNING') : 'STOPPED',
      task_id: live ? last.id : null,
      zone: last.zone,
      started_at: last.started_at,
      stopped_at: live ? null : last.stopped_at,
      tasks: tasks.map(t => ({
        id: t.id, started_at: t.started_at, stopped_at: t.stopped_at ?? null, zone: t.zone,
      })),
    };
  }).filter(Boolean);
}

/**
 * The task holding a copy during the bucket [ts, ts + stepMs): of the tasks running
 * in it, the latest to start (so a deploy hands over at the successor's start), or
 * null when the copy was offline for the whole bucket.
 */
export function holderAt(replica, ts, stepMs) {
  let holder = null;
  (replica?.tasks || []).forEach((t) => {
    if (t.started_at < ts + stepMs && endOf(t) > ts && (!holder || t.started_at > holder.started_at)) {
      holder = t;
    }
  });
  return holder;
}

// ── Series helpers ──────────────────────────────────────────────────────────

/** How a copy is named in menus and legends: "Copy 2 · 3f9c2a1b", "Copy 3 · offline". */
export const replicaLabel = (r) => `Copy ${r.index} · ${r.task_id ? String(r.task_id).slice(0, 8) : 'offline'}`;

/**
 * Turn a MetricBlock into TimeChart rows for one view:
 *   view 'service'  → the service-level series (all tasks, avg or sum)
 *   view <copy id>  → that copy only
 *   view 'compare'  → one column per copy in the window, offline ones included
 * Returns { rows, lines } where rows = [{ t: ISO, <key>: value }].
 */
export function chartRows(block, replicas, view) {
  if (!block || !Array.isArray(block.timestamps)) return { rows: [], lines: [] };
  let keys;
  if (view === 'compare') keys = replicas.map(r => r.id);
  else if (view && view !== 'service') keys = [view];
  else keys = ['service'];
  const labelFor = (k) => {
    if (k === 'service') return block.aggregate === 'sum' ? 'All copies (total)' : 'All copies (average)';
    const r = replicas.find(x => x.id === k);
    return r ? replicaLabel(r) : k;
  };
  const rows = block.timestamps.map((ts, i) => {
    const row = { t: new Date(ts).toISOString() };
    keys.forEach((k) => {
      const v = block.series?.[k]?.[i];
      row[k] = v === undefined ? null : v;
    });
    return row;
  });
  return { rows, lines: keys.map(k => ({ key: k, label: labelFor(k) })) };
}

/** Last non-null value of a series, for "now" readouts under a chart. */
export function lastValue(values) {
  if (!Array.isArray(values)) return null;
  for (let i = values.length - 1; i >= 0; i -= 1) {
    if (values[i] !== null && values[i] !== undefined) return values[i];
  }
  return null;
}

/** Peak (max) of a series, ignoring gaps. */
export function peakValue(values) {
  if (!Array.isArray(values)) return null;
  let max = null;
  values.forEach((v) => { if (v !== null && v !== undefined && (max === null || v > max)) max = v; });
  return max;
}

/** Axis/tooltip time formatter for the platform charts. */
export function timeFormatter(rangeValue) {
  const long = rangeValue === '7d';
  return (iso) => {
    const d = new Date(iso);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    if (!long) return `${hh}:${mm}`;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${hh}:${mm}`;
  };
}

/** Relative age, "4 min ago" / "3 h ago" / "2 d ago". */
export function ago(ms, now = Date.now()) {
  if (!ms) return '—';
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}
